<template>
  <div class="vuetify-overwrite-app fipa-app">
    <v-app>
      <v-main>
        <InitLoad
          v-if="failedToFetch || loading"
          :error="failedToFetch"
          :custom-text="loadingText"
        />
        <App v-else />

        <v-card
          v-show="cookieUsageInfo"
          color="white"
          class="cookie-usage-info elevation-5"
        >
          <v-row>
            <v-col cols="12" sm="8" xl="10" class="black--text">
              {{ $t("common:cookies.text") }}
            </v-col>
            <v-col cols="12" sm="4" xl="2" class="text-center text-md-right">
              <v-btn
                color="accent"
                rounded
                dark
                data-test="cookieUsageConfirm"
                @click="setCookie"
              >
                {{ $t("common:close.button", "Close") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-snackbar
          v-model="connection.offline"
          color="primary-light"
          data-test="offlineSnackbar"
          timeout="-1"
          multi-line
          app
        >
          <div class="d-flex">
            <div class="mr-2 mt-1">
              <v-icon color="primary">$iconExclamationTriangle</v-icon>
            </div>
            <div class="black--text">
              <strong style="font-size: 16px">{{
                $t("common:offlineMode.title", "Offline")
              }}</strong>
              <div>
                {{
                  $t(
                    "common:offlineMode.text",
                    "Your network is unavailable. Please check your data or wifi connection"
                  )
                }}
              </div>
            </div>
          </div>
        </v-snackbar>

        <v-snackbar
          v-if="!embeddedMode"
          v-model="outdatedFrontend"
          data-test="oudatedFrontendSnackbar"
          color="secondary"
          :timeout="-1"
          dark
          app
          height="52px"
        >
          {{
            $t(
              "common:NewVersionAvailable.title",
              "New version is available. Click to update the page"
            )
          }}
          <template #action="{ attrs }">
            <v-btn color="accent" v-bind="attrs" dark @click="refreshUpdate">
              {{
                $t("common:NewVersionAvailable.refreshButton.label", "Update")
              }}
            </v-btn>
          </template>
        </v-snackbar>
      </v-main>
    </v-app>
  </div>
</template>
<script>
import gql from "fraql";
import Cookies from "js-cookie";
import { emailValidationRegex } from "@/mixins/custom_validation";
import { uniq } from "lodash-es";
import i18next from "./plugins/i18next/i18next";
import vuetify from "./plugins/vuetify";
import * as Sentry from "@sentry/vue";
import App from "./App.vue";
import InitLoad from "@/components/skeletons/InitLoad.vue";
import PreLoadMasterDataQuery from "./preLoadMasterDataQuery";
import FipaListener from "@/utils/listenerUtility";
let tokenWorker = null;

function fetchAppVersion(abortController = null) {
  return fetch("/app_version.json", {
    cache: "no-cache",
    redirect: "error",
    headers: {
      "Content-Type": "application/json"
    },
    signal: abortController?.signal ?? undefined
  }).then(response => response.json());
}

export default {
  name: "AppRoot",
  components: { App, InitLoad },
  inject: {
    embeddedMode: {
      default: false
    }
  },
  vuetify,
  data() {
    return {
      fetchVersionAbort: null,

      fullyReady: false,
      userInitDataLoaded: false,

      failedToFetch: false,
      loadingText: null,

      cookieUsageInfo: true,

      connection: {
        offline: false,
        timeout: -1
      },

      outdatedFrontend: false,
      nextRouterReload: false,

      tokenWorker: null,
      appVersion: null,
      appVersionInterval: null
    };
  },
  computed: {
    loading() {
      return (
        this.fullyReady == false ||
        this.userInitDataLoaded == false ||
        this.$apollo.queries.preLoadMasterData.loading
      );
    }
  },
  watch: {
    $route() {
      if (this.nextRouterReload) {
        this.refreshUpdate();
        return;
      }
    }
  },
  beforeCreate() {
    if (tokenWorker) {
      tokenWorker.termiate();
      tokenWorker = null;
    }

    const query = new URLSearchParams(location.search);
    if (query.has("enable_tehnical")) {
      Cookies.set("enable_tehnical", true, {
        secure: process.env.NODE_ENV != "development",
        sameSite: "Strict",
        expires: 7
      });
    }
    if (Cookies.get("enable_tehnical") === "true") {
      window.addEventListener("keydown", event => {
        if (event.repeat) return;
        if (event.altKey && event.ctrlKey) {
          if (event.key == "k" || event.key == "˚") {
            const lang =
              i18next.language == "cimode"
                ? localStorage.getItem("lang") ||
                  document.documentElement.getAttribute("lang")
                : "cimode";

            i18next.changeLanguage(lang, err => {
              // eslint-disable-next-line no-console
              if (err) console.error("something went wrong loading", err);
            });

            document.body.classList.toggle("cimode_enabled");
          }
        }
      });
    }
  },
  created() {
    // this gets called from plugins/router/index in onError handler
    FipaListener.on("routerChunkErrorLoad", () => {
      this.outdatedFrontend = true;
      this.nextRouterReload = true;
    });

    this.cookieUsageInfo =
      Cookies.get("closedCookieInfo") === "true" ? false : true;

    if (!this.embeddedMode) {
      document.addEventListener("visibilitychange", this.handleDocVisibility);
      window.addEventListener("offline", this.handleConnectionLoss);
      window.addEventListener("online", this.handleConnectionLoss);
    }

    window.addEventListener("unhandledrejection", this.unhandleRejection);

    this.$root.$backofficeTools = {
      setNextRouterReload: () => {
        this.nextRouterReload = true;
      },
      triggerRefreshUpdate: this.refreshUpdate,
      showOutdatedFrontendSnackbar: () => {
        this.outdatedFrontend = true;
      }
    };

    document.title = this.$federation.webTitle;

    this.fetchVersionAbort = window.AbortController
      ? new AbortController()
      : null;
    fetchAppVersion(this.fetchVersionAbort).then(({ app_version }) => {
      this.appVersion = app_version;
      this.setupPeriodicalAppVersionCheck();
      window.addEventListener("beforeunload", () => {
        this.fetchVersionAbort?.abort();
      });
    });
  },
  async mounted() {
    if (this.embeddedMode) {
      this.userInitDataLoaded = true;
      this.fullyReady = true;
      return;
    }

    if (
      process.env.VUE_APP_HELPSCOUT_KEY &&
      typeof window.Beacon === "function"
    ) {
      if (this.$vuetify.breakpoint.lgAndUp) {
        window.Beacon("config", {
          display: { style: "iconAndText", position: "left", zIndex: 500 }
        });
      } else {
        window.Beacon("config", {
          display: { style: "icon", position: "left", zIndex: 500 }
        });
      }
    }

    if (sessionStorage.getItem("chunk_reload") === "true") {
      this.loadingText = this.$t(
        "common:snackbarMessage.reloadingPage",
        "Reloading"
      );
      sessionStorage.removeItem("chunk_reload");
    }

    this.fullyReady = true;
  },
  beforeDestroy() {
    window.removeEventListener("unhandledrejection", this.unhandleRejection);
    document.removeEventListener("visibilitychange", this.handleDocVisibility);

    window.removeEventListener("offline", this.handleConnectionLoss);
    window.removeEventListener("online", this.handleConnectionLoss);
  },
  destroyed() {
    if (tokenWorker) tokenWorker = tokenWorker.terminate();
  },
  apollo: {
    userInitData: {
      query: gql`
        query userInitData {
          me {
            id
            name
            phone
            email
            identifier
            licenceCategoryId
            licenceCategory {
              id
              name
            }
            clubName
            permissionsGiven
            canPerformMutationsInScopeAny
            canAccessEntitiesInScopeAny
            allowedEntities
            allowedRoutes
          }

          visitorRole {
            id
            permissionsGiven
            allowedRoutes
            allowedEntities
            mutationsInScopeAny
          }
        }
      `,
      fetchPolicy: "no-cache",
      manual: true,
      skip() {
        return this.embeddedMode;
      },
      result({ data, loading, networkStatus }) {
        if (loading || !data) return;
        if (
          !data.me &&
          this.$auth.checkTokenStillAuthenticated() &&
          networkStatus == 7
        ) {
          Sentry.withScope(scope => {
            scope.setLevel("warning");
            Sentry.captureMessage(
              "Token exists and still valid but object 'me' returned null"
            );
          });
        }
        const { visitorRole, me } = data;

        let userData = {
          id: null,
          name: null,
          phone: null,
          email: null,
          clubName: null,
          identifier: null,
          licenceCategory: null,
          permissionsGiven: visitorRole.permissionsGiven,
          allowedRoutes: visitorRole.allowedRoutes,
          allowedEntities: visitorRole.allowedEntities,
          canPerformMutationsInScopeAny: visitorRole.mutationsInScopeAny
        };

        if (me) {
          // Mix visitorRole permissions with data.me
          userData = Object.assign(userData, me, {
            permissionsGiven: uniq(
              me.permissionsGiven.concat(userData.permissionsGiven)
            ),
            allowedRoutes: uniq(
              me.allowedRoutes.concat(userData.allowedRoutes)
            ),
            allowedEntities: uniq(
              me.allowedEntities.concat(userData.allowedEntities)
            ),
            canPerformMutationsInScopeAny: uniq(
              me.canPerformMutationsInScopeAny.concat(
                userData.canPerformMutationsInScopeAny
              )
            )
          });
        }

        this.$auth.setUserData(userData);

        if (
          this.$auth.isUserLoggedIn &&
          me &&
          process.env.VUE_APP_HELPSCOUT_KEY &&
          typeof window.Beacon === "function"
        ) {
          const identity = {
            id: me.id,
            name: me.name || "-",
            phone: me.phone || "-",
            "licence-category": me.licenceCategory?.name ?? "-",
            "licence-number": me.identifier || "-",
            "representing-club": me.clubName || "-",
            "fipa-cid": this.$auth.getClientId(),
            "fipa-cver": process.env.VUE_APP_RELEASE_VERSION
          };
          const prefill = {
            id: me.id,
            name: me.name || "-"
          };

          if (me.email && RegExp(emailValidationRegex).test(me.email)) {
            identity.email = me.email;
            prefill.email = me.email;
          }

          window.Beacon("identify", identity);
          window.Beacon("prefill", prefill);
        }

        this.userInitDataLoaded = true;
      },
      error(error) {
        if (error.networkError) {
          this.failedToFetch = true;
          this.loadingText = this.$t(
            "common:networkError",
            "System encountered a network error. Please check the network connection and reload the page or try again later."
          );
        }
        if (error.graphQLErrors) {
          this.failedToFetch = true;
          this.loadingText = this.$t(
            "common:tehnicalError",
            "Unfortunately, system encountered a problem. Our technical team is investigating, please try again later."
          );
          Sentry.captureException(error);
        }
      }
    },
    preLoadMasterData: {
      query: PreLoadMasterDataQuery,
      fetchPolicy: "cache-and-network",
      manual: true,
      result() {
        // none
      },
      error(error) {
        if (error.networkError) {
          this.failedToFetch = true;
          this.loadingText = this.$t(
            "common:networkError",
            "System encountered a network error. Please check the network connection and reload the page or try again later."
          );
        }
        if (error.graphQLErrors) {
          this.failedToFetch = true;
          this.loadingText = this.$t(
            "common:tehnicalError",
            "Unfortunately, system encountered a problem. Our technical team is investigating, please try again later."
          );
          Sentry.captureException(error);
        }
      }
    }
  },
  methods: {
    setCookie() {
      Cookies.set("closedCookieInfo", true, {
        expires: 363,
        sameSite: "Strict"
      });
      this.cookieUsageInfo = false;
    },

    handleConnectionLoss(event) {
      switch (event.type) {
        case "online":
          this.connection.offline = false;
          break;
        case "offline":
          this.connection.offline = true;
          break;
      }
    },

    handleDocVisibility() {
      switch (document.visibilityState) {
        case "hidden":
          clearInterval(this.appVersionInterval);
          this.appVersionInterval = null;
          if (tokenWorker) {
            tokenWorker.terminate();
            tokenWorker = null;
          }
          break;
        case "visible":
          this.initTokenCheck();
          this.checkAppVersion();
          this.setupPeriodicalAppVersionCheck();
          break;
      }
    },
    setupPeriodicalAppVersionCheck() {
      if (process.env.NODE_ENV == "development") return;
      this.appVersionInterval = setInterval(this.checkAppVersion, 900000);
    },
    checkAppVersion() {
      if (process.env.NODE_ENV == "development") return;
      fetchAppVersion(this.fetchVersionAbort)
        .then(({ app_version }) => {
          this.outdatedFrontend = app_version != this.appVersion;
        })
        .catch(error => {
          if (error.name === "AbortError") return; // don't report to sentry on aborted
          Sentry.withScope(scope => {
            scope.setLevel("critical");
            scope.setContext("Error details", {
              error
            });
            Sentry.captureMessage(
              "Fetching app_version.json to compare current appVersion failed"
            );
          });
        });
    },
    initTokenCheck() {
      if (this.$auth.isAuthenticated) {
        if (!tokenWorker) {
          tokenWorker = new Worker(
            new URL("@/workers/token_expiracy.js", import.meta.url)
          );
          tokenWorker.addEventListener("message", e => {
            if (e.data.method) {
              this[e.data.method]();
            }
          });

          tokenWorker.postMessage({
            method: "init",
            token: this.$auth.getToken()
          });
        }
      } else if (this.$auth.tokenExists()) {
        this.tokenExpiredMethod();
      }
    },
    tokenExpiredMethod() {
      this.loggingProcess = true;
      // Cases like when user comes back but its expired, prevent page being rendered at all
      this.$auth.logout({ sessionExpired: true });
    },
    renewTokenMethod() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation personRenewToken {
              personRenewToken(input: {}) {
                signInPayload {
                  token
                }
              }
            }
          `
        })
        .then(({ data }) => {
          if (data && data.personRenewToken) {
            this.$auth.setSession(data.personRenewToken.signInPayload.token);
            tokenWorker.postMessage({
              method: "setToken",
              token: data.personRenewToken.signInPayload.token
            });
          }
        })
        .catch(error => {
          this.$sentryThrowError.throwError(error, {
            level: "error"
          });
        });
    },

    unhandleRejection(e) {
      if (
        Cookies.get("enable_tehnical") === true ||
        process.env.NODE_ENV === "development"
      ) {
        e.preventDefault();
        // eslint-disable-next-line no-console
        console.error("Unhandle rejection", e);
      }

      if (
        e.reason?.code == "CSS_CHUNK_LOAD_FAILED" ||
        e.reason?.name?.includes("ChunkLoadError") ||
        e.name?.includes("ChunkLoadError") ||
        e.message?.includes("ChunkLoadError") ||
        e.reason?.message?.includes("Loading CSS chunk")
      ) {
        e.preventDefault();
        this.outdatedFrontend = true;
        this.nextRouterReload = true;
        Sentry.withScope(scope => {
          scope.setLevel("warning");
          scope.setTag("network_error", "chunkFail");
          scope.setContext("Chunk info", e);
          Sentry.captureMessage("Encountered chunk error");
        });
      } else if (e.name && e.message) {
        // If it doesn't have name nor message property, then its likely not a Error instance
        Sentry.withScope(scope => {
          scope.setLevel("error");
          scope.setContext("Error details", {
            error: JSON.stringify(e),
            additionalDetails: { from: "UnhandledRejection" }
          });
          Sentry.captureMessage(e);
        });
      }
    },

    refreshUpdate() {
      sessionStorage.setItem("chunk_reload", true);
      location.reload();
    }
  }
};
</script>
<style lang="scss" scoped>
.cookie-usage-info {
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  padding: 8px 24px;
  z-index: 999999999;
  height: 96px;
  overflow: hidden;
  @media (max-width: 956px) {
    padding: 4px 16px;
    height: auto;
  }
}
</style>
